import React, { useCallback } from "react";
import { MdPerson } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { BiaFitUser, ProfileImage } from "../../types/user";
import { PaginatedResult } from "../../types/api";
import { UserService } from "../../services/userService";
import { ChallengeService } from "../../services/challengeService";
import { useAssignmentLists } from "../../hooks/useAssignmentLists";
import { useAssignmentModalState } from "../../hooks/useAssignmentModalState";
import { useUserSearch } from "../../hooks/useUsers";
import { pageFrom } from "../../hooks/usePaginatedResource";
import AssignmentModalLayout from "../modals/AssignmentModalLayout";
import AssignmentSearch from "../modals/AssignmentSearch";
import AssignmentPanel from "../modals/AssignmentPanel";
import AssignmentItemCard from "../modals/AssignmentItemCard";

// The fields the card renders, common to both user endpoints
interface AssignableUser {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  profile_image?: ProfileImage | string | null;
}

interface UserAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  challengeId: number;
  challengeName: string;
  onUpdate: () => void;
}

const userImageUrl = (user: AssignableUser): string | undefined =>
  typeof user.profile_image === "string"
    ? undefined
    : user.profile_image?.full_path;

const fullName = (user: AssignableUser) =>
  `${user.first_name} ${user.last_name}`;

const UserAssignmentModal: React.FC<UserAssignmentModalProps> = ({
  isOpen,
  onClose,
  challengeId,
  challengeName,
  onUpdate,
}) => {
  const navigate = useNavigate();
  const search = useUserSearch();

  const loadAssigned = useCallback(
    () => ChallengeService.getAllChallengeUsers(challengeId),
    [challengeId]
  );

  const loadAvailablePage = useCallback(
    async (page: number): Promise<PaginatedResult<BiaFitUser> | null> => {
      const response = await UserService.getUsers(page, {});
      if (!response) return null;
      return pageFrom(
        response.users.filter((user) => !user.deleted_at),
        response
      );
    },
    []
  );

  const lists = useAssignmentLists<AssignableUser>({
    isOpen,
    loadAssigned,
    loadAvailablePage,
    search,
    errorMessage: "Failed to load users. Please try again.",
  });

  const modalState = useAssignmentModalState({
    isOpen,
    onClose,
    onUpdate,
    search,
    loadError: lists.error,
  });

  const handleAssign = (user: AssignableUser) =>
    modalState.runAction({
      kind: "assign",
      id: user.id,
      request: () =>
        ChallengeService.assignUsersToChallenge(challengeId, [user.id]),
      onSuccess: () => lists.markAssigned(user),
      successMessage: `${fullName(user)} assigned successfully`,
      failureMessage: "Failed to assign user",
    });

  const handleUnassign = (user: AssignableUser) =>
    modalState.runAction({
      kind: "unassign",
      id: user.id,
      request: () =>
        ChallengeService.unassignUsersFromChallenge(challengeId, [user.id]),
      onSuccess: () => lists.markUnassigned(user),
      successMessage: `${fullName(user)} unassigned successfully`,
      failureMessage: "Failed to unassign user",
    });

  return (
    <AssignmentModalLayout
      isOpen={isOpen}
      onClose={modalState.handleClose}
      title="Manage Users"
      subtitle={challengeName}
      message={modalState.message}
    >
      <AssignmentSearch
        searchTerm={modalState.searchTerm}
        onSearchChange={modalState.setSearchTerm}
        onSearch={modalState.handleSearch}
        onClearSearch={modalState.handleClearSearch}
        placeholder="Search users by name or email..."
        hasSearched={search.hasSearched}
        searchLoading={search.loading}
      />

      <div className="flex flex-col overflow-hidden md:flex-row">
        <AssignmentPanel
          title="Available Users"
          countLabel={
            search.hasSearched
              ? `${lists.availableTotal} users found`
              : `${lists.availableTotal} users`
          }
          tone="available"
          loading={lists.availableLoading}
          isEmpty={lists.available.length === 0}
          emptyIcon={MdPerson}
          emptyTitle={
            modalState.searchTerm
              ? "No users match your search"
              : "No users available"
          }
          onScroll={lists.handleAvailableScroll}
          loadingMore={lists.loadingMore}
          loadingMoreLabel="Loading more users..."
        >
          {lists.available.map((user) => (
            <AssignmentItemCard
              key={`available-${user.id}`}
              id={user.id}
              title={fullName(user)}
              subtitle={user.email}
              imageUrl={userImageUrl(user)}
              isAssigned={false}
              onToggle={() => handleAssign(user)}
              isToggling={modalState.assigningItems.has(user.id)}
            />
          ))}
        </AssignmentPanel>

        <AssignmentPanel
          title="Assigned To Challenge"
          countLabel={`${lists.assigned.length} users`}
          tone="assigned"
          loading={lists.assignedLoading}
          isEmpty={lists.assigned.length === 0}
          emptyIcon={MdPerson}
          emptyTitle="No users assigned"
        >
          {lists.assigned.map((user) => (
            <AssignmentItemCard
              key={`assigned-${user.id}`}
              id={user.id}
              title={fullName(user)}
              subtitle={user.email}
              imageUrl={userImageUrl(user)}
              isAssigned={true}
              onToggle={() => handleUnassign(user)}
              isToggling={modalState.unassigningItems.has(user.id)}
              onClick={(id) => navigate(`/admin/users/${id}`)}
            />
          ))}
        </AssignmentPanel>
      </div>
    </AssignmentModalLayout>
  );
};

export default UserAssignmentModal;
